import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { loadSchedule } from '../data/schedule';

const typeColors = {
  logistics: 'bg-cyan-400',
  keynote: 'bg-primary',
  break: 'bg-amber-400',
  session: 'bg-purple-500',
  workshop: 'bg-emerald-500',
  social: 'bg-rose-500',
};

const typeBadgeColors = {
  logistics: 'bg-cyan-500/10 text-cyan-700 border-cyan-500/20',
  keynote: 'bg-primary/10 text-primary border-primary/20',
  break: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  session: 'bg-purple-500/10 text-purple-700 border-purple-500/20',
  workshop: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20',
  social: 'bg-rose-500/10 text-rose-700 border-rose-500/20',
};

const Program = () => {
  const [schedule, setSchedule] = useState([]);

  useEffect(() => {
    setSchedule(loadSchedule());
  }, []);

  return (
    <div className="min-h-screen pt-24 sm:pt-28 pb-16 sm:pb-20 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center space-x-2 bg-secondary/10 border border-secondary/20 rounded-full px-4 py-1.5 mb-4 sm:mb-5"
          >
            <SafeIcon icon={FiIcons.FiCalendar} className="text-secondary text-sm" />
            <span className="text-sm font-medium text-primary">27 Feb 2026</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xl md:text-2xl font-display text-secondary font-semibold tracking-wide uppercase mb-2"
          >
            Media Roadshow
          </motion.h2>

          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="text-4xl md:text-6xl font-display font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary"
          >
            LAUNCH
          </motion.h1>

          <motion.h2
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl font-display font-bold text-slate-900 uppercase mb-4"
          >
            Program
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="text-slate-500 text-base sm:text-lg max-w-md mx-auto"
          >
            A day filled with innovation, learning, and connection.
          </motion.p>
        </div>

        {/* Timeline */}
        <div className="relative max-w-3xl mx-auto">
          {/* Vertical Line */}
          <div className="hidden md:block absolute left-[27px] top-4 bottom-4 w-1 bg-gradient-to-b from-secondary/20 via-primary/20 to-secondary/20 rounded-full" />

          <div className="space-y-10 sm:space-y-12">
            {schedule.map((item, index) => (
              <motion.article
                key={item.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: index * 0.08, duration: 0.4 }}
                whileHover={{ x: 8, transition: { duration: 0.2 } }}
                className="group relative flex flex-col md:flex-row gap-6 md:items-start"
              >
                {/* Timeline Icon Circle */}
                <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-full bg-white border-4 border-secondary shadow-lg z-10">
                  <div className="text-primary">
                    <SafeIcon name={item.icon} className="text-xl" />
                  </div>
                </div>

                {/* Card */}
                <div className="flex-1 glass-card p-6 rounded-2xl">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-2 gap-2">
                    <span className="inline-block px-3 py-1 rounded-full bg-secondary/10 text-primary font-bold text-sm tracking-wide">
                      {item.time}
                    </span>
                    <span
                      className={`self-start text-[11px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                        typeBadgeColors[item.type] || 'bg-primary/10 text-primary border-primary/20'
                      }`}
                    >
                      {item.type}
                    </span>
                  </div>

                  <h3 className="text-xl font-display font-bold text-slate-900 mb-2 uppercase group-hover:text-primary transition-colors">
                    {item.title}
                  </h3>

                  <div className="h-1 w-20 bg-gradient-to-r from-secondary to-transparent mb-3 rounded-full" />

                  <p className="text-slate-600 font-semibold text-sm leading-relaxed">
                    {item.description}
                  </p>

                  {item.ctaUrl && (
                    <a
                      href={item.ctaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
                    >
                      <SafeIcon name="MessageCircle" className="text-base" />
                      {item.ctaLabel || 'Learn More'}
                    </a>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Program;
